import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

type LimitAction = 'HIDE_FROM_STORE' | 'DEACTIVATE' | 'TRIGGER';

const limitActions: [LimitAction, ...LimitAction[]] = ['HIDE_FROM_STORE', 'DEACTIVATE', 'TRIGGER'];

const channelPassTranslationSchema = z.object({
  languageCode: z
    .string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
  name: z
    .string()
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
  shortDescription: z
    .string()
    .max(500, 'Краткое описание слишком длинное (максимум 500 символов)')
    .nullable()
    .optional(),
  description: z
    .string()
    .max(10000, 'Описание слишком длинное (максимум 10000 символов)')
    .nullable()
    .optional(),
});

export const createChannelPassSchema = z.object({
  channelId: z
    .number({ error: 'ID канала обязателен' })
    .int('ID канала должен быть целым числом')
    .positive('ID канала должен быть положительным'),
  // Ограничения продаж
  hasLimits: z.boolean().default(false),
  userLimit: z
    .number()
    .int('Лимит пользователей должен быть целым числом')
    .positive('Лимит пользователей должен быть положительным')
    .nullable()
    .optional(),
  saleEndDate: z.iso.datetime({ error: 'Некорректный формат даты' }).nullable().optional(),
  onLimitAction: z.enum(limitActions).default('HIDE_FROM_STORE'),
  onLimitTriggerId: z
    .number()
    .int('ID триггера должен быть целым числом')
    .positive('ID триггера должен быть положительным')
    .nullable()
    .optional(),
  onLimitDelayDays: z
    .number()
    .int('Задержка должна быть целым числом')
    .min(0, 'Задержка не может быть отрицательной')
    .default(0),
  isActive: z.boolean().default(true),
  order: z
    .number()
    .int('Порядок должен быть целым числом')
    .min(0, 'Порядок не может быть отрицательным')
    .default(0),
  targetLanguages: z
    .array(
      z.string()
        .min(2, 'Код языка должен быть минимум 2 символа')
        .max(5, 'Код языка должен быть максимум 5 символов'),
    )
    .default([]),
  imageId: z.number().int().positive().nullable().optional(),
  bannerImageId: z.number().int().positive().nullable().optional(),
  translations: z.array(channelPassTranslationSchema).optional(),
});

export const updateChannelPassSchema = createChannelPassSchema.partial();

export const channelPassFiltersSchema = z.object({
  channelId: z.coerce.number().int('ID канала должен быть целым числом').optional(),
  isActive: stringToBooleanSchema,
  showDeleted: stringToBooleanSchema,
  search: z.string().optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'price', 'order', 'isActive']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce
    .number()
    .int('Страница должна быть целым числом')
    .positive('Страница должна быть положительной')
    .default(1),
  limit: z.coerce
    .number()
    .int('Лимит должен быть целым числом')
    .positive('Лимит должен быть положительным')
    .max(500, 'Лимит должен быть <= 500')
    .default(20),
});

export type CreateChannelPassInput = z.infer<typeof createChannelPassSchema>;
export type UpdateChannelPassInput = z.infer<typeof updateChannelPassSchema>;
export type ChannelPassFilters = z.infer<typeof channelPassFiltersSchema>;
